import React, { useState } from "react"
import { Modal, Button, Select, Flex, Input } from "antd"
import { useTranslation } from "react-i18next"
import { CloseOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useMediaQuery } from "react-responsive"
import { CATEGORIES } from "constants/experiences"
import styles from "./index.module.css"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import { CreateCategoryModal } from "../CreateCategory"
import CustomDrawer from "ui/Drawer"

const EditCategoryModal = ({ visible, onClose, onSave, currentCategory }) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" })
  const [selectedCategory, setSelectedCategory] = useState(currentCategory || null)
  const [searchValue, setSearchValue] = useState("")
  const [showCreateCategory, setShowCreateCategory] = useState(false)

  const handleSave = () => {
    onSave(selectedCategory)
  }

  const handleCancel = () => {
    setSelectedCategory(currentCategory || null)
    setSearchValue("")
    setShowCreateCategory(false)
    onClose()
  }

  const handleCreateCategory = () => {
    setShowCreateCategory(true)
  }

  const filteredCategories = CATEGORIES.filter((category) => category.name.toLowerCase().includes(searchValue.toLowerCase()))

  const dropdownRender = (menu) => (
    <div>
      <div className={styles.searchContainer}>
        <Input
          placeholder="Search"
          prefix={<SearchOutlined className={styles.searchIcon} />}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className={styles.searchInput}
        />
      </div>
      {menu}
      <Flex align="center" justify="center">
        <Button type="link" icon={<PlusOutlined />} onClick={handleCreateCategory}>
          Create Category
        </Button>
      </Flex>
    </div>
  )

  const formContent = (
    <>
      <div className={styles.formGroup}>
        <LabeledField labelKey={t("equipment.edit_category_modal.category_label")}>
          <Select
            placeholder={t("equipment.edit_category_modal.select_category_placeholder")}
            value={selectedCategory}
            onChange={setSelectedCategory}
            className={styles.select}
            size="large"
            dropdownRender={dropdownRender}
            showSearch={false}
            options={filteredCategories.map((category) => ({
              value: category.value,
              label: category.name
            }))}
          />
        </LabeledField>
      </div>

      <Flex align="center" justify="space-between" gap={10} className={styles.editCategoryModalButtons}>
        <Button size="large" color="primary" variant="outlined" onClick={handleCancel}>
          {t("equipment.edit_category_modal.cancel_button")}
        </Button>
        <Button size="large" color="primary" variant="solid" onClick={handleSave} disabled={!selectedCategory}>
          {t("equipment.edit_category_modal.changes_button")}
        </Button>
      </Flex>
    </>
  )

  return (
    <>
      {isMobile ? (
        <CustomDrawer open={visible} setOpen={handleCancel} title={t("equipment.edit_category_modal.title")}>
          <div className={styles.container}>{formContent}</div>
        </CustomDrawer>
      ) : (
        <Modal
          open={visible}
          onCancel={handleCancel}
          closeIcon={<CloseOutlined className={styles.closeIcon} />}
          footer={null}
          width={400}
          className={styles.modal}
          centered
          maskClosable={false}
        >
          <div className={styles.container}>
            <h2 className={styles.title}>{t("equipment.edit_category_modal.title")}</h2>
            {formContent}
          </div>
        </Modal>
      )}
      {showCreateCategory && <CreateCategoryModal open={showCreateCategory} onCancel={()=>setShowCreateCategory(false)} />}
    </>
  )
}

EditCategoryModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  currentCategory: PropTypes.string
}

EditCategoryModal.defaultProps = {
  currentCategory: null
}

export default EditCategoryModal
